import { createRouter, createWebHistory } from 'vue-router'
import AppShell from '@/layouts/AppShell.vue'
import { useAuthStore } from '@/stores/auth'
import { pinia } from '@/stores/pinia'
import LandingView from '@/views/LandingView.vue'
import LegalView from '@/views/LegalView.vue'
import AuthView from '@/views/auth/AuthView.vue'
import BillingGateView from '@/views/auth/BillingGateView.vue'
import ProvisioningView from '@/views/auth/ProvisioningView.vue'
import VerifyEmailView from '@/views/auth/VerifyEmailView.vue'
import ManagerView from '@/views/app/ManagerView.vue'
import GoalsView from '@/views/app/GoalsView.vue'
import ExecutionsView from '@/views/app/ExecutionsView.vue'
import AgentsView from '@/views/app/AgentsView.vue'
import WorkflowsView from '@/views/app/WorkflowsView.vue'
import OperationsView from '@/views/app/OperationsView.vue'
import ApprovalsView from '@/views/app/ApprovalsView.vue'
import ConnectionsView from '@/views/app/ConnectionsView.vue'
import KnowledgeView from '@/views/app/KnowledgeView.vue'
import BrowserView from '@/views/app/BrowserView.vue'
import AdvisorView from '@/views/app/AdvisorView.vue'
import UsageView from '@/views/app/UsageView.vue'
import SettingsView from '@/views/app/SettingsView.vue'
import AdminView from '@/views/app/AdminView.vue'
import BillingView from '@/views/app/BillingView.vue'
import CommunicationsView from '@/views/app/CommunicationsView.vue'
import MediaView from '@/views/app/MediaView.vue'
import ProjectDetailView from '@/views/app/ProjectDetailView.vue'
import ProjectsView from '@/views/app/ProjectsView.vue'

const routes = [
  { path: '/', component: LandingView },
  { path: '/privacy', component: LegalView, props: { page: 'privacy' } },
  { path: '/terms', component: LegalView, props: { page: 'terms' } },
  { path: '/login', component: AuthView, props: { mode: 'login' } },
  { path: '/signup', component: AuthView, props: { mode: 'signup' } },
  { path: '/verify-email', component: VerifyEmailView, meta: { requiresAuth: true } },
  { path: '/billing', component: BillingGateView, meta: { requiresAuth: true } },
  { path: '/billing/success', component: BillingGateView, meta: { requiresAuth: true } },
  { path: '/provisioning', component: ProvisioningView, meta: { requiresAuth: true } },
  {
    path: '/app',
    component: AppShell,
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/app/manager' },
      { path: 'manager', component: ManagerView },
      { path: 'projects', component: ProjectsView },
      { path: 'projects/:projectId', component: ProjectDetailView },
      { path: 'goals', component: GoalsView },
      { path: 'agents', component: AgentsView },
      { path: 'workflows', component: WorkflowsView },
      { path: 'executions', component: ExecutionsView },
      { path: 'operations', component: OperationsView },
      { path: 'approvals', component: ApprovalsView },
      { path: 'connections', component: ConnectionsView },
      { path: 'communications', component: CommunicationsView },
      { path: 'media', component: MediaView },
      { path: 'knowledge', component: KnowledgeView },
      { path: 'browser', component: BrowserView },
      { path: 'advisor', component: AdvisorView },
      { path: 'usage', component: UsageView },
      { path: 'billing', component: BillingView },
      { path: 'settings', component: SettingsView },
      { path: 'admin', component: AdminView, meta: { requiresAdmin: true } },
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(async (to) => {
  if (!to.meta.requiresAuth) return true
  const authStore = useAuthStore(pinia)
  const localPreview = import.meta.env.DEV && to.query.preview === '1'
  if (!authStore.isAuthenticated && !localPreview) {
    return { path: '/login', query: { redirect: to.fullPath } }
  }
  if (localPreview || !authStore.user) return true
  const lifecycleRoute = ['/verify-email', '/billing', '/billing/success', '/provisioning'].includes(to.path)
  const status = await authStore.refreshAccountStatus()
  if (!authStore.user.emailVerified && to.path !== '/verify-email') return '/verify-email'
  if (authStore.user.emailVerified && to.path === '/verify-email') return '/billing'
  if (authStore.user.emailVerified && status === 'email_verification_pending') return '/billing'
  if (['payment_required', 'checkout_pending'].includes(status) && !to.path.startsWith('/billing')) return '/billing'
  if (status === 'provisioning' && to.path !== '/provisioning') return '/provisioning'
  if (to.path.startsWith('/app') && status !== 'active') return status === 'provisioning' ? '/provisioning' : '/billing'
  if (lifecycleRoute && status === 'active') return '/app/manager'
  if (to.meta.requiresAdmin && authStore.user && !(await authStore.user.getIdTokenResult()).claims.admin) return '/app/manager'
  return true
})
